import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  UmpireComponentSurvey,
  UmpireSurvey,
  UmpireSurveyAnswer,
} from '@tisa-group/models';
import {ImageVariantModule} from '@tisa-group/core';
import {SvgIconComponent} from '@tisa-group/client-ui/components/svg-icon';
import {TranslateModule} from '@ngx-translate/core';
import {SURVEY_HANDLERS} from './survey-handlers.token';

@Component({
  selector: 'tisa-survey',
  imports: [CommonModule, ImageVariantModule, SvgIconComponent, TranslateModule],
  templateUrl: './survey.component.html',
  styleUrl: './survey.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyComponent {
  private handlers = inject(SURVEY_HANDLERS, {optional: true});

  readonly component = input<UmpireComponentSurvey | undefined>();

  readonly survey = computed(() => {
    return this.component()?.survey;
  });

  readonly localUserAnswer = computed(() => {
    return this.handlers?.localUserAnswer() ?? null;
  });

  readonly totalAnswerCount = computed(() => {
    const baseCount = this.survey()?.total_answers_count ?? 0;
    // Add 1 to total if we have a local answer
    return this.localUserAnswer() ? baseCount + 1 : baseCount;
  });

  readonly isAnswered = computed(() => {
    // Check local answer first, then server answer
    return !!this.localUserAnswer() || !!this.survey()?.user_answer;
  });

  readonly userAnswerId = computed(() => {
    // Prefer local answer ID
    const localAnswer = this.localUserAnswer();
    if (localAnswer?.id) {
      return localAnswer.id;
    }
    return this.survey()?.user_answer?.answer?.id;
  });

  readonly isUserLoggedIn = computed(() => {
    return this.handlers?.isUserLoggedIn() ?? false;
  });

  readonly showAnswers = computed(() => {
    const survey = this.survey();
    return this.isAnswered() || survey?.status === 'ended';
  });

  readonly surveyStatus = computed(() => {
    const survey = this.survey();
    if (!survey) return '';

    if (survey.status === 'ended') {
      return 'survey.status.ended';
    }

    if (survey.status === 'active') {
      return 'survey.status.active';
    }

    return '';
  });

  calculatePercentage(answer: UmpireSurveyAnswer): number {
    const total = this.totalAnswerCount();
    if (total === undefined || total === 0) {
      return 0;
    }

    let answerCount = answer?.answers_count || 0;

    // If this is the local user's answer, add 1 to its count
    const localAnswer = this.localUserAnswer();
    if (localAnswer && localAnswer.id === answer.id) {
      answerCount += 1;
    }

    return Math.round((answerCount / total) * 100);
  }

  onAnswerClick(answer: UmpireSurveyAnswer): void {
    if (!this.isUserLoggedIn()) {
      this.onLoginClick();
      return;
    }

    if (this.showAnswers()) {
      return;
    }

    this.handlers?.onAnswerSelected(answer);
  }

  onLoginClick(): void {
    this.handlers?.onLoginRequested();
  }
}
